import { FC, useEffect, useMemo, useRef, useState, } from "react";
// 组件
import { ProButton, ProDropdown, ProModal, ProSpin, } from "../antd";
import TableTransfer from "./tableTransfer";
// 方法
import classNames from "classnames";
import { dataSourceClassification, filterObjectFilednames } from "../utils/dataUtils";
import { getMoveDataSource } from "../EdiTable/util";
import { responseMessage } from '../utils/messageUtils';
import { paramsFilterOptions } from "../SelectTab/util";
// 常量
import { SUCCESS_STATUS_CODE, STATUS_CREATED, STATUS_CREATED_CODE, TABLE_COLUMN_EMPTY, } from "../constants/constant";
import { DEFAULT_LABEL_KEY, DEFAULT_ROW_KEY, SORT_FIELDNAME, SuperTransferProps } from "./interface";
import { API_STATUS_ENUM } from "../constants/enum";
import { TransferDirection } from "antd/lib/transfer";
import { TransferKey } from "antd/lib/transfer/interface";
import "./style.less";

/** 穿梭表单 */
const SuperTransfer: FC<SuperTransferProps> = props => {
  const {
    rowKey, columns, dataKey, transferKey, otherParams, value: outValue, buttonText, required, labelKey, disabled,
    modalTitle, listWidth = 300, listHeight, isMoveSort, sortFieldname, searchFieldnames, otherExtractFieldnames,
    changeEmptyValue, fetchApi, beforeFetch, filterData, onChange,
  } = props;
  const [open, setOpen] = useState<boolean>(false);
  const [value, setValue] = useState<any[]>();
  const [loading, setLoading] = useState<boolean>(false); // 是否处于加载中状态
  const [dataSource, setDataSource] = useState<any[]>(); // 数据源
  const [selectedDataSource, setSelectedDataSource] = useState<any[]>(); // 选中数据集
  const apiStatus = useRef<API_STATUS_ENUM>(API_STATUS_ENUM.NOT_START); // 接口状态
  const timer = useRef<any>(); // 延时过滤选项定时器
  const otherParamsString = otherParams ? JSON.stringify(otherParams) : '';
  useEffect(() => {
    setValue(outValue);
  }, [outValue]);
  useEffect(() => {
    if (apiStatus.current === API_STATUS_ENUM.SUCCESS || apiStatus.current === API_STATUS_ENUM.ERROR) {
      changeEmptyValue && setValue(undefined);
      apiStatus.current = API_STATUS_ENUM.NOT_START;
    }
  }, [otherParamsString]);
  /** 获取远程数据 */
  const handleFetchApi = async() => {
    if (!fetchApi) {
      setDataSource(undefined);
      return;
    }
    let newParams: any = { ...otherParams };
    // 是否需要从缓存中读取
    if (transferKey) {
      const CACHE_OPTIONS = window.sessionStorage.getItem(transferKey);
      if (CACHE_OPTIONS) {
        if (CACHE_OPTIONS === API_STATUS_ENUM.LOADING) {
          timer.current && clearInterval(timer.current);
          timer.current = setInterval(() => {
            const resData = window.sessionStorage.getItem(transferKey);
            if (resData && resData !== API_STATUS_ENUM.LOADING) {
              clearInterval(timer.current);
              const data = JSON.parse(resData);
              handleDataProcessing(data);
            }
          }, 100);
        } else {
          const data = JSON.parse(CACHE_OPTIONS);
          handleDataProcessing(data);
        }
        return;
      } else {
        window.sessionStorage.setItem(transferKey, API_STATUS_ENUM.LOADING);
      }
    }
    // 请求之前参数处理
    if (beforeFetch) {
      newParams = beforeFetch(newParams);
      if (newParams === false) {
        if (transferKey) {
          const CACHE_OPTIONS = window.sessionStorage.getItem(transferKey);
          if (CACHE_OPTIONS === API_STATUS_ENUM.LOADING) {
            window.sessionStorage.removeItem(transferKey);
          }
        }
        return;
      }
    }
    setLoading(true);
    apiStatus.current = API_STATUS_ENUM.LOADING;
    const res = await fetchApi(transferKey ? {} : newParams);
    setLoading(false);
    if (res.code === SUCCESS_STATUS_CODE) {
      const data = (dataKey ? res.data?.[dataKey] : res.data) || [];
      // 数据存入缓存
      if (transferKey) {
        window.sessionStorage.setItem(transferKey, JSON.stringify(data || []));
      }
      handleDataProcessing(data)
      apiStatus.current = API_STATUS_ENUM.SUCCESS;
      return;
    }
    apiStatus.current = API_STATUS_ENUM.ERROR;
  };
  /** 远程获取的数据处理 */
  const handleDataProcessing = (data: any = []) => {
    const newData = transferKey ? paramsFilterOptions(data, otherParams) : data;
    const result: any[] = filterData ? filterData(newData) : newData;
    const valueFieldname = rowKey || DEFAULT_ROW_KEY;
    const newSelectedDataSource: any[] = [];
    const newDataSource: any[] = [];
    result.forEach(item => {
      const itemKey = item[valueFieldname];
      const newItem = {
        ...item,
        key: itemKey,
        [STATUS_CREATED_CODE]: STATUS_CREATED.DEFAULT,
      };
      newDataSource.push(newItem);
    });
    if (Array.isArray(outValue) && outValue.length) {
      outValue.forEach(item => {
        const itemKey = item[valueFieldname];
        const newItem = {
          ...item,
          key: itemKey,
          [STATUS_CREATED_CODE]: STATUS_CREATED.DEFAULT,
        };
        const findItem = result.find(v => v[valueFieldname] === itemKey);
        if (!findItem) {
          newDataSource.push(newItem);
        }
        newSelectedDataSource.push({...findItem, ...newItem});
      });
    }
    setDataSource(newDataSource);
    setSelectedDataSource(newSelectedDataSource);
  };
  /** 点击按钮 */
  const handleClick = () => {
    if ((apiStatus.current === API_STATUS_ENUM.NOT_START || apiStatus.current === API_STATUS_ENUM.ERROR)) {
      handleFetchApi();
    }
    setOpen(true);
  };
  /** 关闭 */
  const handleCancel = () => {
    setOpen(false);
  };
  /** 确认 */
  const handleOk = () => {
    const { effectiveDataSource, deleteDataSource, } = dataSourceClassification(selectedDataSource);
    if (required && !effectiveDataSource.length) {
      responseMessage('warning', '至少需要选择一条数据');
      return;
    }
    const valueFieldname = rowKey || DEFAULT_ROW_KEY;
    const filterNames: string[] = [labelKey || DEFAULT_LABEL_KEY];
    if (otherExtractFieldnames) {
      if (typeof otherExtractFieldnames === 'string') {
        filterNames.push(otherExtractFieldnames);
      }
      if (Array.isArray(otherExtractFieldnames)) {
        filterNames.push(...otherExtractFieldnames);
      }
    }
    const newValue: any[] = [
      ...(deleteDataSource.map(item => ({
        ...filterObjectFilednames(item, filterNames),
        [valueFieldname]: item[valueFieldname],
        [STATUS_CREATED_CODE]: item[STATUS_CREATED_CODE],
      }))),
      ...(effectiveDataSource.map((item, index) => {
        const newItem: any = {
          ...filterObjectFilednames(item, filterNames),
          [valueFieldname]: item[valueFieldname],
          [STATUS_CREATED_CODE]: item[STATUS_CREATED_CODE],
        };
        if (isMoveSort) {
          newItem[sortFieldname || SORT_FIELDNAME] = index;
        }
        return newItem;
      })),
    ];
    setValue(newValue.length ? newValue : undefined);
    onChange?.(newValue.length ? newValue : undefined);
    handleCancel();
  };
  /** 选项在两栏之间转移时的回调函数 */
  const handleChange = (_: TransferKey[], direction: TransferDirection, moveKeys: TransferKey[]) => {
    if (direction === 'right') {
      const newSelectedDataSource = selectedDataSource ? [...selectedDataSource] : [];
      dataSource?.forEach(item => {
        if (moveKeys.includes(item.key)) {
          newSelectedDataSource.push({
            ...item,
            [STATUS_CREATED_CODE]: STATUS_CREATED.ADD,
          });
        }
      });
      setSelectedDataSource(newSelectedDataSource);
      return;
    }
    if (direction === 'left') {
      const newSelectedDataSource: any[] = [];
      selectedDataSource?.forEach(item => {
        if (moveKeys.includes(item.key)) {
          if (item[STATUS_CREATED_CODE] !== STATUS_CREATED.ADD) {
            newSelectedDataSource.unshift({
              ...item,
              [STATUS_CREATED_CODE]: STATUS_CREATED.DELETE,
            });
          }
        } else {
          newSelectedDataSource.push(item);
        }
      });
      setSelectedDataSource(newSelectedDataSource);
    }
  };
  const handleDragEnd = (startKey: string, endKey: string) => {
    const startIndex = selectedDataSource?.findIndex(item => item.key === startKey);
    const endIndex = selectedDataSource?.findIndex(item => item.key === endKey);
    if (startIndex === undefined || endIndex === undefined || !selectedDataSource) return;
    const newSelectedDataSource: any[] = getMoveDataSource(selectedDataSource, startIndex, endIndex);
    setSelectedDataSource(newSelectedDataSource);
  }
  /** modal宽度 */
  const modalWidth = useMemo(() => {
    return (listWidth * 2) + 88;
  }, [listWidth]);
  const renderValue = useMemo(() => {
    const effectiveValue = value?.filter(item => item[STATUS_CREATED_CODE] !== STATUS_CREATED.DELETE);
    if (Array.isArray(effectiveValue) && effectiveValue.length) {
      const labelFieldname = labelKey || DEFAULT_LABEL_KEY;
      const classnames = classNames("yb-transfer-render", { disabled: !!disabled, });
      if (effectiveValue.length === 1) {
        return (
          <span className={classnames}>{effectiveValue[0][labelFieldname]}</span>
        );
      }
      const titleValue = effectiveValue[0][labelFieldname];
      const valueKey = rowKey || DEFAULT_ROW_KEY;
      return disabled ? (
        <div
          className="yb-line-ellipsis yb-transfer-render-disabled"
          title={effectiveValue.map(item => item[labelFieldname]).join()}
        >{effectiveValue.map(item => item[labelFieldname]).join()}</div>
      ) : (
        <ProDropdown
          menu={{
            items: effectiveValue.map(item => {
              return {
                key: item[valueKey],
                label: item[labelFieldname],
              };
            }),
          }}
        >
          <div className={classnames}>
            <span>{titleValue}</span>
            <span className="yb-transfer-render-num">+{effectiveValue.length - 1}</span>
          </div>
        </ProDropdown>
      );
    }
    return disabled ? TABLE_COLUMN_EMPTY : null;
  }, [value, disabled]);

  return (
    <>
      <div className="yb-transfer-wrapper">
        {
          !disabled && <ProButton className="yb-transfer-button" onClick={handleClick}>{buttonText}</ProButton>
        }
        {renderValue}
      </div>
      <ProModal
        title={modalTitle}
        open={open}
        width={modalWidth}
        maskClosable={false}
        onCancel={handleCancel}
        onOk={handleOk}
      >
        <ProSpin spinning={loading}>
          <TableTransfer
            rowKey={rowKey}
            columns={columns}
            dataSource={dataSource}
            listHeight={listHeight}
            listWidth={listWidth}
            isMoveSort={isMoveSort}
            selectedDataSource={selectedDataSource}
            searchFieldnames={searchFieldnames}
            onChange={handleChange}
            onDragEnd={handleDragEnd}
          />
        </ProSpin>
      </ProModal>
    </>
  );
};

SuperTransfer.defaultProps = {
  rowKey: DEFAULT_ROW_KEY,
  buttonText: '选择',
  modalTitle: '请选择',
  listWidth: 300,
  listHeight: 400,
  isMoveSort: true,
  sortFieldname: SORT_FIELDNAME,
  labelKey: DEFAULT_LABEL_KEY,
};

export default SuperTransfer;
